// クラシック版のエンジン（common/js/charat/chibi.js）から使う中身の窓口 window.charatCore（2026-10-23〜、段階 B）。
// tools/chibi-unify/build-ui.mjs が common/js/charat/charat.core.min.js にまとめる。クラシック版のページは chibiConfig.CORE が true のメーカーだけ読み、
// エンジンはキャラの組み立て（makeCharactor）をここに任せる。画面の部品（d3 のボタン・一覧・画面ファイル）はクラシック版のまま。
// クラシック版の画面はレイヤーの g の属性（param*）を読むので、描いたあとに同じ値を書いておく（mirror。makeCharactor が書いていたのと同じ値）
import { C, blankLine, catByKey, layerG } from './model.js';
import { keyOf } from './util.js';
import { applyZoom, place, placeOf, useLayers } from './render.js';
import { applySave, saveObject } from './savedata.js';
import { findPart, layersOf, partsList } from './data.js';
import { classicClearPattern, classicColor, classicGrad, classicMove, classicOpacity, classicPart, classicPrev, classicRev, classicRotate, classicSetPattern, classicSyncParts, classicSyncPattern, classicZoom, lineOf, replaceState } from './ops.js';
import { PTN_ADJUST, PTN_EX1, PTN_EX2, PTN_SIZE, PTN_STROKE1, PTN_STROKE2, classicPatternFound } from './pattern.js';
import { on } from './events.js';
import type { Category, CategoryData, PartState, PartsCache, SaveData } from '../types.js';
import type { ClassicPattern, CoreHooks } from './model.js';

export interface ClassicAttach {
  /** getcategory.php の中身（エンジンが読んだもの） */
  data: CategoryData;
  /** パーツ（エンジンが展開した chibi.partsCache。形は中身と同じ { NAME: { SVG } }） */
  partsCache: PartsCache;
  /** うすさ・柄の defs を置く SVG（#chaSvgSave） */
  svg: SVGSVGElement;
  /** レイヤー名 → レイヤーの g（背景だけ #chaSvgBG、ほかは #chaSvgMake） */
  find: (layer: string) => SVGGElement | null;
  /** 画面の拡大（chibi.scMake の倍率） */
  scale: number;
  /** 背景のカテゴリの先頭レイヤー名（chibi.PARTS_BG。ランダムでは付けない） */
  bgLayer: string;
  /** パーツを替えても位置・回転を保つ（chibiConfig の KEEP_MOVE_ON_PARTS_CHANGE） */
  keepMove?: boolean;
  hooks?: CoreHooks;
  /** 柄（エンジンの柄の一覧・defs・描き方。段階 B4） */
  pattern: ClassicPattern;
}

/** クラシック版の操作（段階 B3。エンジンの changeParts・reverseG・zoomG・moveG・selGrad・setAllGradColor の代わり） */
export interface ClassicOps {
  /** パーツを替える。resetScale は差しかえの動き 1（背景のほか）で拡大を 1 に戻すか */
  part(cat: Category, id: number, resetScale: boolean): void;
  /** 反転する・戻す（delay・duration は reverseG と同じ。省略で 0・150ms） */
  rev(cat: Category, on: boolean, delay?: number, duration?: number): void;
  /** 1段大きく・小さく（step・max は zoomG と同じ 0.02・0.2。duration は省略で 150ms、0 ならすぐ） */
  zoom(cat: Category, isZoom: boolean, step: number, max: number, duration?: number): void;
  /** ずらす（両方 0 なら 0 に戻す） */
  move(cat: Category, dx: number, dy: number): void;
  /** 色（side 0 全体・1 左・2 右。org は「元の色」のボタンのときの [基本色, 影色]） */
  color(cat: Category, hex: string, side: number, org: [string, string] | null): void;
  /** グラデーションの上・下 */
  grad(cat: Category, from: string, to: string, side: number): void;
  /** 柄を付ける（setPattern。sel は選んでいるカテゴリ、group はそのグループ＝chibi.groupCategory） */
  pattern(sel: Category, group: Category[], id: string): void;
  /** 柄を外す（clearPattern） */
  clearPattern(sel: Category, group: Category[]): void;
  /** 色の入れかえを1段進める（btnRevColor） */
  revColor(sel: Category, group: Category[]): void;
  /** 回す（rotateByDelta。degree は ±5 など、反転中は向きを逆に） */
  rotate(cat: Category, degree: number): void;
  /** うすさ（OpacityG。0〜1、付けるのは 200ms 後） */
  opacity(cat: Category, opacity: number): void;
  /** パーツ番号を各カテゴリの先頭レイヤーの属性（paramParts）に合わせる（エンジンの外の後処理が書きかえたあと） */
  syncParts(): void;
  /** 回転しているカテゴリの位置を付け直す（組み立てのあとの後処理で要素が隠れ、回転の中心が変わるため） */
  replaceRotated(): void;
}

/** window.charatCore（クラシック版のエンジンが使う） */
export interface CharatCore {
  attach(o: ClassicAttach): void;
  load(save: SaveData): void;
  random(): void;
  saveObject(): SaveData;
  mirror(cat: Category): void;
  mirrorAll(): void;
  state(): Record<string, PartState>;
  classic: ClassicOps;
  on: typeof on;
}

let bgLayer = 'bg';
let keepMove = false;

function attach(o: ClassicAttach) {
  C.data = o.data;
  C.partsCache = o.partsCache;
  C.cats = o.data.CATEGORY;
  C.cats.forEach((c) => { catByKey[keyOf(c)] = c; });
  C.scale = o.scale;
  C.hooks = o.hooks || {};
  bgLayer = o.bgLayer;
  keepMove = !!o.keepMove;
  C.classicPattern = o.pattern;
  useLayers(o.svg, o.find);
}

// クラシック版の画面が読む属性を、今の状態と同じ値に（makeCharactor の「全レイヤーを初期値に → カテゴリの値」と同じ結果）。
// パーツ番号・色・柄は先頭レイヤーにだけ、位置・反転・倍率・回転・うすさはカテゴリの全レイヤーに。
// 柄が一覧にあって貼れたときは、柄の値もカテゴリの全レイヤーに（setPartsColorPattern）
function mirror(cat: Category) {
  const st = C.state[keyOf(cat)], k = C.scale;
  const drawn = !!(st && st.parts != null && findPart(cat, st.parts));
  const rev = drawn && Number(st.rev) === 1 && (cat.REVERSE == 1 || cat.IS_REV != 0);
  cat.LAYER.forEach((name, i) => {
    const g = layerG[name];
    if (!g) return;
    const set = (attr: string, v: string | number) => { g.setAttribute(attr, String(v)); };
    const head = i === 0 && drawn;
    const ptnAll = head || (drawn && classicPatternFound(st.ptn));
    set('paramParts', head ? st.parts! : '');
    set('paramTx', drawn && cat.IS_MOVE != 0 ? Number(st.tx) || 0 : 0);
    set('paramTy', drawn && cat.IS_MOVE != 0 ? Number(st.ty) || 0 : 0);
    set('paramScx', (rev ? -1 : 1) * k);
    set('paramScy', k);
    set('paramRev', rev ? 1 : 0);
    set('paramScale', drawn && cat.IS_ZOOM != 0 ? Number(st.scale) || 1 : 1);
    set('paramCol', head ? st.col : '');
    set('paramColFrom', head ? st.from : '');
    set('paramColTo', head ? st.to : '');
    set('paramColLFrom', head ? st.lfrom : '');
    set('paramColLTo', head ? st.lto : '');
    set('paramColRFrom', head ? st.rfrom : '');
    set('paramColRTo', head ? st.rto : '');
    set('paramPtn', ptnAll ? st.ptn : '');
    set('paramPrev', ptnAll ? Number(st.prev) || 0 : 0);
    set('paramPtnMode', ptnAll ? Number(st.ptnmode) || 0 : 0);
    set('paramRotate', drawn ? Number(st.rotate) || 0 : 0);
    set('paramOpacity', drawn && st.opacity !== '' && st.opacity != null ? st.opacity : 1);
    // パーツの無いレイヤーは、位置も初期値に（makeCharactor が全レイヤーに付けていた形）
    if (!drawn) set('transform', 'translate(0, 0), rotate(0,0,0), scale(' + k + ',' + k + ')');
  });
}
function mirrorAll() { C.cats.forEach(mirror); }

// 保存データでキャラを組み立てる（makeCharactor(aChara, false, true) と同じ）
function load(save: SaveData) {
  applySave(save);
  mirrorAll();
}
// ランダム（makeCharactor(全カテゴリの名前だけ, true, true) と同じ）: charat.draw.js の randomPart で選んだパーツを、RND が 1 以上のカテゴリにだけ付ける。
// 色・反転・位置は付けない（持ちもの（右）も反転しない）。背景（キーが PARTS_BG）は付けない。
// 選ぶのはエンジンと同じく背景以外の全カテゴリで、カテゴリの並びの順（RND が 1 以下なら Math.random を1つ使う。付けない RND 0 のカテゴリも。
// 同じ乱数なら同じキャラになるように）
function random() {
  const next: Record<string, PartState> = {}, seen: Record<string, boolean> = Object.create(null);
  C.cats.forEach((cat) => {
    const key = keyOf(cat);
    if (seen[key] || key === bgLayer) return;
    seen[key] = true;
    const part = charatDraw.randomPart(cat, partsList(cat.NAME));
    if (!part || !(cat.RND >= 1)) return;
    const st = blankLine();
    st.parts = part.id;
    next[key] = st;
  });
  replaceState(next);
  mirrorAll();
}

// ---- クラシック版の操作（段階 B3、2026-10-23〜） ----
// 状態は ops.ts の classic* で書きかえ、ここでは前のエンジンと同じ手順で描いて、前のエンジンが書いていたレイヤーの属性（param*）を同じように書く。
// 前のエンジンは操作ごとに書く属性が違う（色はカテゴリの全レイヤー、読み込みは先頭のレイヤーだけ）。クラシック版の画面と後処理（skinColorLink など）が
// その属性を読むので、読み込みの mirror（全部そろえ直す）は使わず、変わった属性だけを前と同じ所に書く
const layersOfCat = (cat: Category) => cat.LAYER.map((name) => layerG[name] || null);
function setAttrs(cat: Category, attrs: Record<string, string | number>) {
  layersOfCat(cat).forEach((g) => {
    if (!g) return;
    for (const k in attrs) g.setAttribute(k, String(attrs[k]));
  });
}

// パーツを替える（setPartsList のボタン → changeParts の差しかえと、そのあとの setGradColor）。
// 差しかえ: 各レイヤーの前のパーツを外して層を入れ、パーツの g は動きの始まりの形に（差しかえの動きと後処理はエンジン）。
// 位置・回転は KEEP_MOVE_ON_PARTS_CHANGE でなければ、層のあるレイヤーだけ 0 に。拡大は resetScale なら 1 に。
// 色は先頭のレイヤーの値で塗り直す（setGradColor と同じ: 全体の色、左・右の色（全体の色が無くても）、グラデーションは上と下の両方があれば）
function classicPartOp(cat: Category, id: number, resetScale: boolean) {
  const part = findPart(cat, id);
  const pieces = part ? layersOf(cat, part) : cat.LAYER.map(() => null);
  const st = classicPart(cat, Number(id), !keepMove && !!pieces[0], resetScale);
  const layers = layersOfCat(cat);
  layers.forEach((g, i) => {
    if (!g) return;
    // 前のエンジンは最初の子だけ外していた（レイヤーの中はパーツの g 1つ）
    if (g.firstElementChild) g.removeChild(g.firstElementChild);
    const src = pieces[i];
    if (!src) return;
    charatDraw.putPart(g, src).setAttribute('transform', 'translate(0, 0), scale(1, 1), rotate(0)');
    if (!keepMove) { g.setAttribute('paramTx', '0'); g.setAttribute('paramTy', '0'); g.setAttribute('paramRotate', '0'); }
  });
  if (layers[0]) layers[0].setAttribute('paramParts', String(id));
  if (resetScale) setAttrs(cat, { paramScale: 1 });
  if (cat.COLOR.length > 0) {
    if (st.col) layers.forEach((g) => { charatDraw.colorLayer(g, cat, st.col, 0); });
    if (st.lfrom) layers.forEach((g) => { charatDraw.colorLayer(g, cat, st.lfrom, 1); });
    if (st.rfrom) layers.forEach((g) => { charatDraw.colorLayer(g, cat, st.rfrom, 2); });
  }
  if (cat.GRAD.length > 0 && st.from && st.to) layers.forEach((g) => { charatDraw.gradLayer(g, cat, st.from, st.to); });
}
// 反転（reverseG）: 全レイヤーの横の倍率の向き・反転の値を書き、動きは差しこみ口（反転する前の位置から）
function classicRevOp(cat: Category, on: boolean, delay?: number, duration?: number) {
  const prev = placeOf(cat, lineOf(cat));
  const st = classicRev(cat, on);
  setAttrs(cat, { paramScx: (on ? -1 : 1) * C.scale, paramScy: C.scale, paramRev: on ? 1 : 0 });
  place(cat, st, 'rev', { prev: prev, delay: delay, duration: duration });
}
// 拡大・縮小（zoomG）: 今の倍率（先頭のレイヤーの値＝状態）から1段。全レイヤーに書いて、パーツの g に付ける
function classicZoomOp(cat: Category, isZoom: boolean, step: number, max: number, duration?: number) {
  const next = charatDraw.nextScale(Number(lineOf(cat).scale) || 1, isZoom, step, max);
  const st = classicZoom(cat, next);
  setAttrs(cat, { paramScale: next });
  applyZoom(cat, st, 'zoom', { duration: duration });
}
// 移動（moveG）: 全レイヤーに位置を書いて付け直す（0,0 は位置を戻す。戻すときだけ動きがある）
function classicMoveOp(cat: Category, dx: number, dy: number) {
  const st = classicMove(cat, dx, dy);
  setAttrs(cat, { paramTx: st.tx, paramTy: st.ty });
  place(cat, st, dx === 0 && dy === 0 ? 'reset' : 'move');
}
// 色（selGrad の色の所。グループのカテゴリごとにエンジンが呼ぶ）: 全レイヤーに書いて、変わる側だけ塗る（changeColor と同じ）
function classicColorOp(cat: Category, hex: string, side: number, org: [string, string] | null) {
  classicColor(cat, hex, side);
  if (side === 1) setAttrs(cat, { paramColLFrom: hex, paramColLTo: hex });
  else if (side === 2) setAttrs(cat, { paramColRFrom: hex, paramColRTo: hex });
  else setAttrs(cat, { paramCol: hex, paramColFrom: hex, paramColTo: hex, paramColLFrom: '', paramColLTo: '', paramColRFrom: '', paramColRTo: '' });
  if (cat.COLOR[0] === undefined) return;
  layersOfCat(cat).forEach((g) => { charatDraw.colorLayer(g, cat, hex, side, org || undefined); });
}
// グラデーション（setAllGradColor。グループのカテゴリごとにエンジンが呼ぶ）
function classicGradOp(cat: Category, from: string, to: string, side: number) {
  classicGrad(cat, from, to, side);
  const attrs: Record<string, string> = { paramColFrom: from, paramColTo: to };
  if (side === 1) { attrs.paramColLFrom = from; attrs.paramColLTo = to; } else if (side === 2) { attrs.paramColRFrom = from; attrs.paramColRTo = to; }
  setAttrs(cat, attrs);
  layersOfCat(cat).forEach((g) => { charatDraw.gradLayer(g, cat, from, to); });
}

// 柄を付ける（setPattern）: 選んでいるカテゴリの色・色の入れかえで、その先頭レイヤーの名前の <pattern> を2つ作り（copyPattern）、
// 一覧にあればグループのカテゴリの全レイヤーに柄の値を書いて、色の入れかえ（PATTERN_REV_PARTS のとき）と柄を貼る（changePattern）。
// 柄の地はいつも選んでいるカテゴリの色（読み込みの PATTERN_PARTS_COLOR は効かない。前のエンジンと同じ）。キャラ全体のカテゴリは柄の番号だけ
function classicPatternOp(sel: Category, group: Category[], id: string) {
  const cp = C.classicPattern!;
  const before = lineOf(sel);
  const rev = Number(before.prev) || 0;
  const mode = before.ptn ? Number(before.ptnmode) || 0 : rev;
  const base = 'cpattern_' + sel.LAYER[0], dark = 'cpatterndk_' + sel.LAYER[0];
  const defs = cp.defs(sel);
  const found = defs
    ? charatDraw.copyPattern(cp.list || [], defs, base, dark, id, rev, mode, PTN_EX1, PTN_EX2, PTN_STROKE1, PTN_STROKE2, before.col, PTN_SIZE, PTN_ADJUST)
    : classicPatternFound(id);
  const targets = found && sel.LAYER[0] !== cp.charaLayer ? group : [];
  classicSetPattern(sel, targets, id);
  const head = layersOfCat(sel)[0];
  if (head) { head.setAttribute('paramPtnMode', String(mode)); head.setAttribute('paramPtn', id); }
  targets.forEach((cat) => {
    setAttrs(cat, { paramPtn: id, paramPtnMode: mode, paramPrev: rev });
    if (cp.revParts) layersOfCat(cat).forEach((g) => { charatDraw.revColorLayer(g, rev, PTN_EX1, PTN_EX2, PTN_STROKE1, PTN_STROKE2); });
    layersOfCat(cat).forEach((g) => { charatDraw.patternLayer(g, cat, base, dark, mode, PTN_EX1, PTN_EX2); });
  });
}
// 柄を外す（clearPattern）: 先頭レイヤーとグループのカテゴリの全レイヤーの柄の値を空にして、元の塗りに戻す（色の入れかえは戻さない。前のエンジンと同じ）。
// 柄なしのボタン（selPattern）がこのあと色を塗り直すのはエンジン
function classicClearPatternOp(sel: Category, group: Category[]) {
  const chara = sel.LAYER[0] === C.classicPattern!.charaLayer;
  classicClearPattern(sel, chara ? [] : group);
  const head = layersOfCat(sel)[0];
  if (head) { head.setAttribute('paramPtn', ''); head.setAttribute('paramPtnMode', '0'); head.setAttribute('paramPrev', '0'); }
  if (chara) return;
  group.forEach((cat) => {
    setAttrs(cat, { paramPtn: '', paramPtnMode: 0, paramPrev: 0 });
    layersOfCat(cat).forEach((g) => { charatDraw.patternLayer(g, cat, null, null, 0, PTN_EX1, PTN_EX2); });
  });
}
// 色の入れかえ（btnRevColor）: 0 → 1 → …（MODE_COLREV の次で 0）。柄があれば選んでいるカテゴリの値を進めて柄を付け直す。
// 柄が無ければグループのカテゴリのレイヤーごとに進めて（読み込みのあとは先頭のレイヤーだけ保存データの値・ほかは 0 なので、レイヤーの属性から進める）、
// 最後のレイヤーの値でカテゴリの全レイヤーの色を入れかえる（PATTERN_REV_PARTS は見ない。前のエンジンと同じ）
function classicRevColorOp(sel: Category, group: Category[]) {
  const cp = C.classicPattern!;
  const step = (v: unknown) => { const r = (Number(v) || 0) + 1; return r > cp.revMax ? 0 : r; };
  const st = lineOf(sel);
  if (st.ptn) {
    const r = step(st.prev);
    classicPrev(sel, r);
    const head = layersOfCat(sel)[0];
    if (head) head.setAttribute('paramPrev', String(r));
    classicPatternOp(sel, group, st.ptn);
    return;
  }
  group.forEach((cat) => {
    let last = 0;
    layersOfCat(cat).forEach((g, i) => {
      if (!g) return;
      last = step(g.getAttribute('paramPrev'));
      g.setAttribute('paramPrev', String(last));
      if (i === 0) classicPrev(cat, last);
    });
    layersOfCat(cat).forEach((g) => { charatDraw.revColorLayer(g, last, PTN_EX1, PTN_EX2, PTN_STROKE1, PTN_STROKE2); });
  });
}

// 回転（rotateByDelta、段階 B5）: キャラの SVG（#chaSvgSave）にあるレイヤーごとに、今の回転の値に足す（反転しているレイヤーは逆向き。鏡像なので見た目の回転方向をボタンに合わせる）。
// 背景（別の SVG）は回さない（前のエンジンと同じ）。状態は先頭のレイヤーの値（前のエンジンも保存は先頭のレイヤーから）。位置は差しこみ口でその場で付ける
function classicRotateOp(cat: Category, degree: number) {
  const layers = layersOfCat(cat).filter((g) => g && C.svg.contains(g)) as SVGGElement[];
  if (!layers.length) return;
  const head = layersOfCat(cat)[0];
  layers.forEach((g) => {
    const r = Number(g.getAttribute('paramRotate')) + (Number(g.getAttribute('paramRev')) ? -degree : degree);
    g.setAttribute('paramRotate', String(r));
    if (g === head) classicRotate(cat, r);
  });
  place(cat, lineOf(cat), 'rotate');
}
// うすさ（OpacityG。スライダーを動かすたび）: 状態はすぐ、描くのと属性はキャラの SVG にあるレイヤーごとに 200ms 後（前のエンジンと同じ）
function classicOpacityOp(cat: Category, opacity: number) {
  const layers = layersOfCat(cat).filter((g) => g && C.svg.contains(g)) as SVGGElement[];
  if (!layers.length) return;
  classicOpacity(cat, opacity);
  cat.LAYER.forEach((name) => {
    const g = layerG[name];
    if (!g || layers.indexOf(g) < 0) return;
    setTimeout(() => {
      charatDraw.opacityLayer(C.svg, g, name, opacity);
      g.setAttribute('paramOpacity', String(opacity));
    }, 200);
  });
}

// パーツ番号をレイヤーの属性に合わせる（段階 B5）: メーカー専用の後処理（kawaii・似顔絵の手の排他、似顔絵の髪型の男女の排他）が、エンジンの外で
// 先頭レイヤーの paramParts を書きかえる（隠したカテゴリを 1 や 0 に）。クラシック版の保存データはこの属性から作るので、中身の状態もそれに合わせる。
// 中身の操作の前・保存データを作る前に毎回見る（髪型の排他は、パーツのクリックのあとにページの jQuery が書きかえる）
function syncParts() {
  C.cats.forEach((cat) => {
    const g = layerG[cat.LAYER[0]];
    if (!g) return;
    const attr = g.getAttribute('paramParts');
    const st = C.state[keyOf(cat)];
    const now = st && st.parts != null ? String(st.parts) : '';
    if ((attr || '') !== now) classicSyncParts(cat, attr ? Number(attr) : null);
    // 第1世代の柄（mono）は柄の操作をエンジンの画面ファイルがするので、柄・色の入れかえの値も先頭レイヤーの属性から写す
    if (C.classicPattern && C.classicPattern.engineDraws) {
      const line = C.state[keyOf(cat)];
      const ptn = g.getAttribute('paramPtn') || '', prev = Number(g.getAttribute('paramPrev')) || 0, mode = Number(g.getAttribute('paramPtnMode')) || 0;
      if (!line ? ptn || prev || mode : line.ptn !== ptn || (Number(line.prev) || 0) !== prev || (Number(line.ptnmode) || 0) !== mode) classicSyncPattern(cat, ptn, prev, mode);
    }
  });
}
// 回転しているカテゴリの位置を付け直す（組み立てのあと、エンジンの後処理のあとに。2026-10-24、段階 B5）。
// 回転の中心はカテゴリの全レイヤーを合わせた範囲のまん中で、後処理（アイシャドウなど）が要素を隠すと変わる。前のエンジンは読み込みでカテゴリごとに
// 後処理を通ってから回していたので、隠したあとの範囲の中心だった（kawaii の目）
function replaceRotated() {
  C.cats.forEach((cat) => {
    const st = C.state[keyOf(cat)];
    if (!st || st.parts == null || !(Number(st.rotate) || 0) || !findPart(cat, st.parts)) return;
    place(cat, st, 'load');
  });
}
// 中身の操作の入口（どれも先にパーツ番号を属性に合わせる）
function synced<A extends unknown[]>(fn: (...args: A) => void) {
  return (...args: A) => {
    syncParts();
    fn(...args);
  };
}

const api: CharatCore = {
  attach: attach,
  load: load,
  random: random,
  saveObject: () => {
    syncParts();
    return saveObject();
  },
  mirror: mirror,
  mirrorAll: mirrorAll,
  state: () => C.state,
  classic: {
    part: synced(classicPartOp),
    rev: synced(classicRevOp),
    zoom: synced(classicZoomOp),
    move: synced(classicMoveOp),
    color: synced(classicColorOp),
    grad: synced(classicGradOp),
    pattern: synced(classicPatternOp),
    clearPattern: synced(classicClearPatternOp),
    revColor: synced(classicRevColorOp),
    rotate: synced(classicRotateOp),
    opacity: synced(classicOpacityOp),
    syncParts: syncParts,
    replaceRotated: replaceRotated,
  },
  on: on,
};
window.charatCore = api;
